import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';

const REASONS = [
  { value: 'photos_missing', label: 'Photos manquantes' },
  { value: 'photos_blurry', label: 'Photos floues' },
  { value: 'signature_illegible', label: 'Signature illisible' },
  { value: 'data_wrong', label: 'Données erronées (poids, heures)' },
  { value: 'reserves_incomplete', label: 'Réserves incomplètes' },
  { value: 'other', label: 'Autre' },
];

const PARTIES_LOADING = [
  { value: 'shipper', label: 'Représentant départ' },
  { value: 'driver', label: 'Chauffeur' },
];
const PARTIES_UNLOADING = [
  { value: 'consignee', label: 'Représentant arrivée' },
  { value: 'driver', label: 'Chauffeur' },
];

/**
 * PM Spec V3 13/05/2026 — CorrectionRequestModal
 * Permet à l'opérateur Baobab d'émettre une demande de correction.
 */
const CorrectionRequestModal = ({
  open, onOpenChange, noteType = 'loading',
  submitting = false, onSubmit,
}) => {
  const [reasons, setReasons] = useState([]);
  const [customNote, setCustomNote] = useState('');
  const [targetParties, setTargetParties] = useState([]);
  const [escalate, setEscalate] = useState(false);

  const partiesAvailable = noteType === 'loading' ? PARTIES_LOADING : PARTIES_UNLOADING;

  const toggle = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };

  const handleSubmit = () => {
    onSubmit?.({
      reasons,
      customNote: customNote || null,
      targetParties,
      escalateToAdmin: escalate,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="correction-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" /> Demander une correction
          </DialogTitle>
          <DialogDescription>
            Les parties ciblées devront re-signer après correction.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Quoi corriger ?</div>
            <div className="grid grid-cols-1 gap-1.5">
              {REASONS.map(r => (
                <label key={r.value}
                       className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reasons.includes(r.value)}
                    onChange={() => toggle(reasons, setReasons, r.value)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid={`correction-reason-${r.value}`}
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Textarea
              data-testid="correction-custom-note"
              placeholder="Précisions (optionnel)"
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Renvoyer à :</div>
            <div className="grid grid-cols-1 gap-1.5">
              {partiesAvailable.map(p => (
                <label key={p.value}
                       className="flex items-center gap-2 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={targetParties.includes(p.value)}
                    onChange={() => toggle(targetParties, setTargetParties, p.value)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid={`correction-party-${p.value}`}
                  />
                  <span className="text-sm">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 p-2 rounded-md bg-amber-50 cursor-pointer">
            <input
              type="checkbox" checked={escalate}
              onChange={(e) => setEscalate(e.target.checked)}
              className="w-4 h-4 accent-amber-600"
              data-testid="correction-escalate"
            />
            <ShieldAlert className="w-4 h-4 text-amber-700" />
            <span className="text-sm">Escalade super-admin (cas exceptionnel)</span>
          </label>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}
                  data-testid="correction-cancel">
            Annuler
          </Button>
          <Button
            data-testid="correction-submit"
            disabled={submitting || reasons.length === 0 || targetParties.length === 0}
            onClick={handleSubmit}
            className="bg-amber-600 hover:bg-amber-700 gap-1.5"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            Confirmer la demande
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectionRequestModal;
